import React from "react";
import { motion } from "framer-motion";
import { Check, Star } from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";

const packages = [
  {
    id: "basico",
    name: "Básico",
    price: 6500,
    description: "Ideal para emprendedores que necesitan presencia profesional en línea.",
    features: [
      "Sitio web de hasta 5 secciones",
      "Diseño responsive (móvil y escritorio)",
      "Formulario de contacto",
      "Integración con WhatsApp",
      "Optimización básica SEO",
    ],
    highlight: false,
  },
  {
    id: "profesional",
    name: "Profesional",
    price: 10500,
    description: "Para negocios establecidos que buscan destacar y atraer más clientes.",
    features: [
      "Sitio web de hasta 10 secciones / páginas",
      "Diseño a la medida con tu identidad",
      "Galería de productos o servicios",
      "Blog o sección de noticias",
      "SEO avanzado + Google Analytics",
      "Capacitación para auto-administrar",
    ],
    highlight: true,
  },
  {
    id: "premium",
    name: "Premium",
    price: 18500,
    description: "Solución completa para marcas que buscan máxima calidad y resultados.",
    features: [
      "Páginas ilimitadas con diseño exclusivo",
      "Catálogo o tienda en línea avanzada",
      "Integración con sistemas de pago",
      "Animaciones y experiencia premium",
      "Soporte prioritario 3 meses",
      "Estrategia y consultoría inicial",
    ],
    highlight: false,
  },
];

const fmt = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 })
    .format(n);

const Paquetes = () => {
  return (
    <div data-testid="page-packages">
      <section className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-block w-12 h-px bg-[#D4AF37]" />
              <span className="text-xs tracking-[0.3em] uppercase text-[#D4AF37]">
                Paquetes y precios
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white leading-tight">
              Elige el paquete <span className="text-[#D4AF37]">ideal para tu negocio</span>
            </h1>
            <p className="mt-6 text-zinc-300 text-base md:text-lg leading-relaxed">
              Precios claros, sin sorpresas. Todos los paquetes incluyen diseño
              profesional, sitio responsive y atención personalizada.
            </p>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
            {packages.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`pkg-${p.id}`}
                className={`relative flex flex-col bg-[#0d0d0d] p-8 md:p-10 transition-colors ${
                  p.highlight
                    ? "border-2 border-[#D4AF37]"
                    : "border border-white/10 hover:border-[#D4AF37]/50"
                }`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-black text-xs tracking-[0.25em] uppercase font-bold px-4 py-1 inline-flex items-center gap-1">
                    <Star className="w-3 h-3" fill="currentColor" />
                    Más elegido
                  </div>
                )}

                <div className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-3">
                  {p.name}
                </div>
                <div className="flex items-end gap-1">
                  <div className="font-display text-4xl md:text-5xl text-white tracking-tight">
                    {fmt(p.price)}
                  </div>
                  <div className="text-zinc-400 text-sm mb-2">MXN</div>
                </div>
                <p className="mt-4 text-zinc-400 leading-relaxed">{p.description}</p>

                <ul className="mt-8 space-y-3 mb-10">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-zinc-200">
                      <Check className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" strokeWidth={2} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto flex flex-col gap-3">
                  <WhatsAppButton
                    testId={`pkg-wa-${p.id}`}
                    label={`Contratar ${p.name}`}
                    variant={p.highlight ? "primary" : "outline"}
                    message={`Hola Rincon Web, me interesa el paquete ${p.name} (${fmt(p.price)} MXN). ¿Cómo iniciamos?`}
                  />
                </div>
              </motion.div>
            ))}
          </div>

          <p className="mt-12 text-center text-zinc-500 text-sm">
            * Precios en pesos mexicanos (MXN). ¿Necesitas algo distinto?{" "}
            <a href="/contacto" className="text-[#D4AF37] hover:text-white">
              Cuéntanos tu proyecto
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
};

export default Paquetes;
